'use client'

import { useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { authClient } from '@/lib/auth'
import { isPublicPath, onAuthFailure } from '@/lib/auth-failure'
import { BACKEND_URL, client } from '@/lib/orpcClient'
import { onWorkspaceReadOnly } from '@/lib/workspace-readonly'

/**
 * Tenant lifecycle status, mirrored from the backend whoami output.
 * `null` is treated as "active" by the banner — that's the case for
 * unauthenticated requests or public paths where the auth middleware
 * never resolved a tenant.
 */
export type TenantStatus = 'active' | 'paused' | 'pending_deletion' | null

interface User {
  user: {
    id: string
    email: string
    name: string
    role: string | null
  } | null
  org: {
    id: string | null
    name: string
    /** Tenant lifecycle status — drives the read-only banner. */
    status: TenantStatus
    /** ISO timestamp; non-null only when status is 'pending_deletion'. */
    scheduledDeleteAt: string | null
  }
}

interface UserContextValue {
  loading: boolean
  current: User | null
  logout: () => Promise<void>
  setUser: React.Dispatch<React.SetStateAction<User | null>>
  init: () => Promise<void>
  /**
   * Re-run whoami without showing the loading state. Used by the
   * 423 listener so a write-blocked response triggers a fresh
   * status fetch and the banner appears within seconds — not on
   * the next page load.
   */
  refresh: () => Promise<void>
}

const UserContext = createContext<UserContextValue | undefined>(undefined)

export function useUser(): UserContextValue {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

/**
 * Full logout flow:
 *  1. Tell better-auth to destroy the session (best-effort)
 *  2. Hit the backend to clear the tenant-scoping cookies (oid/uid)
 *  3. Clear the TanStack Query cache so stale data can't render
 *  4. Hard-navigate to /login so every module/provider tree resets
 *
 * Every step is best-effort — we don't want a network blip to trap
 * the user in a broken state. This is the recovery path, so resilience
 * matters more than cleanliness.
 */
async function performFullLogout(clearQueries: () => void) {
  try {
    await authClient.signOut()
  } catch (err) {
    console.warn('[logout] better-auth signOut failed:', err)
  }
  try {
    await fetch(`${BACKEND_URL}/api/v1/auth/clear-tenant-cookies`, {
      method: 'POST',
      credentials: 'include',
    })
  } catch (err) {
    console.warn('[logout] clear-tenant-cookies failed:', err)
  }
  try {
    clearQueries()
  } catch {
    // ignore
  }
  if (typeof window !== 'undefined') {
    // Hard navigate — nukes all React state and re-mounts from scratch.
    // This is intentional: a broken-session recovery should not try to
    // keep in-flight queries or route state alive.
    window.location.replace('/login')
  }
}

export function UserProvider(props: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()
  const hasInitialized = useRef(false)

  const logout = useCallback(async () => {
    setUser(null)
    await performFullLogout(() => queryClient.clear())
  }, [queryClient])

  const init = useCallback(async () => {
    setLoading(true)
    try {
      const currentUser = await client.user.whoami()
      if (!currentUser?.user) {
        // whoami returned a null-user envelope — backend says we're not
        // authenticated. If we're on a public page, that's expected and
        // we just clear local state. If we're on a protected page, force
        // a full logout + redirect so the user can re-sign in cleanly.
        setUser(null)
        if (typeof window !== 'undefined' && !isPublicPath(window.location.pathname)) {
          await performFullLogout(() => queryClient.clear())
        }
        return
      }
      setUser(currentUser)
    } catch (err) {
      // Any error during whoami means we can't trust the session at all.
      // On a public page: clear state, let the user log in. On a
      // protected page: force the full logout recovery.
      console.warn('[user-provider] whoami failed:', err)
      setUser(null)
      if (typeof window !== 'undefined' && !isPublicPath(window.location.pathname)) {
        await performFullLogout(() => queryClient.clear())
      }
    } finally {
      setLoading(false)
    }
  }, [queryClient])

  /**
   * Re-run whoami without flipping `loading`. Called when a write
   * gets a 423 response — the tenant status has changed underneath
   * us and we want the banner to appear immediately, but we DON'T
   * want every protected page to flash a loading skeleton just
   * because the user clicked Save on a paused workspace.
   *
   * Errors are swallowed: if the refresh fails we keep the previous
   * state. The user can still navigate / refresh manually if they
   * want to recover — the worst case here is a slightly-stale banner.
   */
  const refresh = useCallback(async () => {
    try {
      const currentUser = await client.user.whoami()
      if (currentUser?.user) {
        setUser(currentUser)
      }
    } catch (err) {
      console.warn('[user-provider] refresh failed:', err)
    }
  }, [])

  // Fire init once on mount. Guard with a ref so StrictMode's double-mount
  // doesn't fire whoami twice.
  useEffect(() => {
    if (hasInitialized.current) return
    hasInitialized.current = true
    void init()
  }, [init])

  // Subscribe to global auth-failure events. Any oRPC 401 anywhere in the
  // app triggers performFullLogout via this handler.
  useEffect(() => {
    return onAuthFailure(() => {
      setUser(null)
      void performFullLogout(() => queryClient.clear())
    })
  }, [queryClient])

  // Subscribe to workspace-readonly events fired by the oRPC client
  // when a request returns 423 WORKSPACE_READ_ONLY. Refreshing whoami
  // pulls the new status into the UserProvider so the banner renders
  // immediately — no page reload required.
  useEffect(() => {
    return onWorkspaceReadOnly(() => {
      void refresh()
    })
  }, [refresh])

  return (
    <UserContext.Provider
      value={{
        loading,
        current: user,
        logout,
        setUser,
        init,
        refresh,
      }}
    >
      {props.children}
    </UserContext.Provider>
  )
}
